import { createSelector, createSlice } from "@reduxjs/toolkit";
import { generateClient } from 'aws-amplify/api';
import * as queries from "../graphql/queries";
import { reset } from "./globalActions";
import * as mutations from "../graphql/mutations";
import { orderBy } from "lodash";

const client = generateClient();

const moduleName = "folder";

const initialState = {
    loading: false,
    folders: {},
    active: "submitted",
    open: [],
    nextToken: null,
};

const userSlice = createSlice({
    name: moduleName,
    initialState,
    extraReducers: (builder) => {
        builder.addCase(reset, () => initialState);
    },
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setFolders: (state, action) => {
            state.folders[action.payload.parentId] = action.payload.folders;
        },
        addFolder: (state, action) => {
            state.folders[action.payload.parentId].push(action.payload);
        },
        onDeleteFolder: (state, action) => {
            if (state.folders[action.payload.parentId]) {
                const idx = state.folders[action.payload.parentId].findIndex((f) => f.id === action.payload.id);
                state.folders[action.payload.parentId] = [
                    ...state.folders[action.payload.parentId].slice(0, idx),
                    ...state.folders[action.payload.parentId].slice(idx + 1),
                ];
            }
        },
        toggleFolder: (state, action) => {
            const idx = state.open.indexOf(action.payload);
            if (idx >= 0) {
                state.open = [...state.open.slice(0, idx), ...state.open.slice(idx + 1)]
            } else {
                state.open.push(action.payload);
            }
        },
        setActiveFolder: (state, action) => {
            state.active = action.payload;
        }
    },
});

export const { setLoading, setFolders, addFolder, onDeleteFolder, toggleFolder, setActiveFolder } = userSlice.actions;
export default userSlice.reducer;

export const selectState = (state) => state[moduleName];
export const selectLoading = createSelector(selectState, (state) => state.loading);
export const selectFolders = createSelector(selectState, (state) => state.folders);
export const selectActiveFolder = createSelector(selectState, (state) => state.active);
export const selectFolderOpen = createSelector([selectState, (state, folderId) => folderId], (state, folderId) => state.open.indexOf(folderId) >= 0)
export const selectFoldersByParentId = (parentId) => createSelector(selectFolders, (folders) => orderBy(folders[parentId], (f) => f.name?.toLowerCase()));

export const fetchFolders =
    (parentId = "root") =>
        async (dispatch) => {
            try {
                dispatch(setLoading(true));
                const { data } = await client.graphql({
                    query: queries.foldersByParentId,
                    variables: {
                        parentId,
                    },
                });
                dispatch(setFolders({ parentId, folders: data.foldersByParentId.items }));
                return data.foldersByParentId.items;
            } finally {
                dispatch(setLoading(false));
            }
        };

export const newFolder = (folder) => async (dispatch, getState) => {
    try {
        dispatch(setLoading(true));
        const { data } = await client.graphql({
            query: mutations.createFolder,
            variables: {
                input: {
                    ...folder,
                },
            },
        });
        dispatch(addFolder(data.createFolder));
        return data.createFolder;
    } catch (ex) {
        console.log("failed to create document", ex);
    } finally {
        dispatch(setLoading(false));
    }
};

export const deleteFolder = (folderId) => async (dispatch) => {
    try {
        const { data } = await client.graphql({
            query: mutations.deleteFolder,
            variables: {
                input: {
                    id: folderId,
                },
            },
        });
        dispatch(onDeleteFolder(data.deleteFolder));
        return true;
    } catch (ex) {
        console.log("failed to delete folder", ex);
    }
};
